import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchSubjects,
  fetchSubjectsAdmin,
  createSubject,
  updateSubject,
  deleteSubject,
} from "../service/subjectService";
import type { Subject, CreateSubjectPayload, UpdateSubjectPayload } from "../types";

export const loadSubjects = createAsyncThunk("subjects/loadSubjects", async () => {
  const data = await fetchSubjects();
  return data;
});

/** Super Admin catalog page — every subject, active or not, full detail. */
export const loadSubjectsAdmin = createAsyncThunk(
  "subjects/loadSubjectsAdmin",
  async (params: { page: number; limit: number; search?: string }) => fetchSubjectsAdmin(params)
);

export const addSubject = createAsyncThunk(
  "subjects/addSubject",
  async (payload: CreateSubjectPayload, { rejectWithValue }) => {
    try {
      return await createSubject(payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error ?? "Failed to add subject");
    }
  }
);

export const editSubject = createAsyncThunk(
  "subjects/editSubject",
  async ({ id, payload }: { id: number; payload: UpdateSubjectPayload }, { rejectWithValue }) => {
    try {
      return await updateSubject(id, payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error ?? "Failed to update subject");
    }
  }
);

export const removeSubject = createAsyncThunk(
  "subjects/removeSubject",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteSubject(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error ?? "Failed to delete subject");
    }
  }
);

interface SubjectState {
  subjects: Subject[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  /** Bumped after an add/delete/import so the catalog page refetches its current page. */
  reloadKey: number;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: SubjectState = {
  subjects: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  reloadKey: 0,
  loading: false,
  saving: false,
  error: null,
};

const subjectSlice = createSlice({
  name: "subjects",
  initialState,
  reducers: {
    clearSubjectError(state) {
      state.error = null;
    },
    reloadSubjects(state) {
      state.reloadKey += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSubjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSubjects.fulfilled, (state, action) => {
        state.loading = false;
        state.subjects = action.payload;
      })
      .addCase(loadSubjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load subjects";
      })
      .addCase(loadSubjectsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSubjectsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.subjects = action.payload.subjects;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadSubjectsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load subjects";
      })
      .addCase(addSubject.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(addSubject.fulfilled, (state) => {
        state.saving = false;
        state.reloadKey += 1;
      })
      .addCase(addSubject.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? action.error.message ?? "Failed to add subject";
      })
      .addCase(editSubject.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(editSubject.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.subjects.findIndex((s) => s.id === action.payload.id);
        if (idx !== -1) state.subjects[idx] = action.payload;
      })
      .addCase(editSubject.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? action.error.message ?? "Failed to update subject";
      })
      .addCase(removeSubject.fulfilled, (state) => {
        state.reloadKey += 1;
      })
      .addCase(removeSubject.rejected, (state, action) => {
        state.error = (action.payload as string) ?? "Failed to delete subject";
      });
  },
});

export const { clearSubjectError, reloadSubjects } = subjectSlice.actions;
export default subjectSlice.reducer;
